import React, {
  ChangeEvent,
  FocusEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { connect, useDispatch } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import { setRoom } from '@store/actionSlices/appConfig'
import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { TokenPayloadInterface, setToken } from '@store/actionSlices/token'
import { DEFAULT_SHOWCASE_ROOM } from '@store/constants'
import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import Container from '@components/container'

import {
  useAuthUserMutation,
  useAuthViaTokenMutation,
} from '@api/authentication'
import { ShowcaseRoomItem } from '@api/showcase-room'

import {
  removeCacheVersion,
  setCacheVersion,
} from '@utilities/cache-version-utils'
import InputHandler from '@utilities/form-util'
import { getQueryStringParams } from '@utilities/helper'
import ResetUtil from '@utilities/reset-util'
import useResolveMasterKeyRoom from '@utilities/useResolveMasterKeyRoom'

import { fetchCacheVersionFromFirebase } from '@firebaseUtil/firebase-cache-version-service'
import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { LinkSvg } from '@svg/react'

import { getClientInformation } from '@adUtilities/client-information'

import QRAuth from './qr-auth'

export interface LoginProps {
  projectIdentity: ProjectIdentity
}

function Login({ projectIdentity }: LoginProps) {
  const dispatch = useDispatch()
  const location = useLocation()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKeys: projectIdentity.sessionId,
  })
  const resetUtil = ResetUtil()

  const [authenticateUser, { data, error }] = useAuthUserMutation()
  const [authenticateViaToken, { data: tokenData, error: tokenError }] =
    useAuthViaTokenMutation()
  const resolveMasterKeyRoom = useResolveMasterKeyRoom()

  const [serverErrorMessage, setServerErrorMessage] = useState('')

  const [formValid, setFormValid] = useState(false)

  const [authFail, setAuthFail] = useState(false)

  const [isLoading, setIsLoading] = useState(false)

  const [projectName, setProjectName] = useState({
    value: '',
    isValid: true,
    message: '',
  })

  const [password, setPassword] = useState({
    value: '',
    isValid: true,
    message: '',
  })

  const [email, setEmail] = useState({
    value: '',
    isValid: true,
    message: '',
  })

  const qrAuth = useMemo(() => {
    const { qrAuth = '' } = getQueryStringParams(location.search)
    return qrAuth === 'true'
  }, [location.search])

  const ANALYTICS_DASHBOARD_REDIRECT_URL =
    process.env.REACT_APP_ANALYTICS_DASHBOARD_HOST || ''

  const handleProjectNameFocus = useCallback(() => {
    setProjectName((prev) => ({ ...prev, isValid: true, message: '' }))
    setAuthFail(false)
    setServerErrorMessage('')
  }, [])

  const handleEmailFocus = useCallback(() => {
    setEmail((prev) => ({ ...prev, isValid: true, message: '' }))
    setAuthFail(false)
    setServerErrorMessage('')
  }, [])

  const handlePasswordFocus = useCallback(() => {
    setPassword((prev) => ({ ...prev, isValid: true, message: '' }))
    setAuthFail(false)
    setServerErrorMessage('')
  }, [])

  const handleProjectNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setProjectName((prev) => ({
        ...prev,
        value: e.target.value,
        isValid: true,
        message: '',
      }))
      setAuthFail(false)
    },
    []
  )

  const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setEmail((prev) => ({
      ...prev,
      value: e.target.value,
      isValid: true,
      message: '',
    }))
    setAuthFail(false)
  }, [])

  const handlePasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setPassword((prev) => ({
        ...prev,
        value: e.target.value,
        isValid: true,
        message: '',
      }))
      setAuthFail(false)
    },
    []
  )

  const handleProjectNameBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      InputHandler(e, setProjectName)
    },
    []
  )

  const handleEmailBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    InputHandler(e, setEmail)
  }, [])

  const handlePasswordBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    InputHandler(e, setPassword)
  }, [])

  useEffect(() => {
    if (
      !password.isValid ||
      !email.isValid ||
      !password.value ||
      !email.value ||
      isLoading
    ) {
      setFormValid(false)
      return
    }
    setFormValid(true)
  }, [password, email, isLoading])

  const authProcedure = async ({
    tokenPayload,
    showcaseRoom,
    urlRoomParam,
    masterKey,
    projectName,
  }: {
    tokenPayload: TokenPayloadInterface
    showcaseRoom?: ShowcaseRoomItem | null
    urlRoomParam?: string
    masterKey?: string
    projectName?: string
  }) => {
    setIsLoading(false)

    let resolvedRoom =
      urlRoomParam || showcaseRoom?.name || DEFAULT_SHOWCASE_ROOM

    if (masterKey) {
      const masterKeyRoom = await resolveMasterKeyRoom(projectName || '', {
        masterKey,
        userId: tokenPayload._id,
        accessToken: tokenPayload.access_token.token,
      })

      if (masterKeyRoom) {
        resolvedRoom =
          masterKeyRoom || showcaseRoom?.name || DEFAULT_SHOWCASE_ROOM
      }
    }

    dispatch(setRoom(resolvedRoom))
    dispatch(setToken(tokenPayload))
  }

  const triggerLogin = async () => {
    const clientInformation = await getClientInformation()
    authenticateUser({
      project_canonical_name: projectName.value,
      password: password.value,
      email: email.value,
      clientInformation: { ...clientInformation, client: 'showcase' },
    })
  }

  const login = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    const exists = await firebaseControlQuery.isProjectEnabled(
      projectName.value
    )
    if (exists) {
      triggerLogin()
      setAuthFail(false)
      return
    }
    setIsLoading(false)
    setAuthFail(true)
  }

  const validateSessionArray = (sessionListJson: string) => {
    try {
      const sessionListArray = JSON.parse(sessionListJson)
      if (sessionListArray instanceof Array) {
        return sessionListArray.filter(
          (sesh: any) => sesh?.key && sesh?.project
        )
      }
    } catch (e) {
      console.error('Invalid Session List.')
    }
    return []
  }

  const handleSessionListFireStore = async (
    sessionId: { project: string; key: string }[]
  ) => {
    await Promise.all(
      sessionId.map(async ({ project, key }) => {
        if (projectName.value === project) {
          return
        }
        await firebaseControlQuery.createSession({
          projectName: project,
          sessionId: key,
        })
      })
    )
  }

  const handleAutoAuthProcedure = async () => {
    if (tokenData && tokenData.data) {
      const {
        data: {
          user,
          project: { canonical_name: activeProject },
          showcaseRoom,
        },
      } = tokenData

      const token = user as TokenPayloadInterface

      const { sessionKey, room, sessionList, masterKey } = getQueryStringParams(
        location.search
      )

      const sessionIdArray = validateSessionArray(sessionList)

      if (sessionKey) {
        sessionIdArray.push({
          key: sessionKey,
          project: activeProject,
        })
      }

      await handleSessionListFireStore(sessionIdArray)

      removeCacheVersion()
      const projectName = tokenData.data.project.canonical_name
      const version = await fetchCacheVersionFromFirebase(projectName)
      setCacheVersion(version)

      dispatch(
        setProjectIdentity({
          projectName: tokenData.data.project.canonical_name,
          projectId: tokenData.data.project.id,
          projectLabel: tokenData.data.project.name,
          sessionId: sessionIdArray,
          masterKey: masterKey ?? '',
        })
      )
      authProcedure({
        tokenPayload: token,
        showcaseRoom,
        urlRoomParam: room,
        masterKey,
        projectName: tokenData.data.project.canonical_name,
      })
    }
  }

  const runAuthWithCacheVersion = async () => {
    if (!data?.data) {
      return
    }

    removeCacheVersion()
    const projectName = data.data.project.canonical_name
    const version = await fetchCacheVersionFromFirebase(projectName)
    setCacheVersion(version)

    dispatch(
      setProjectIdentity({
        projectName: data.data.project.canonical_name,
        projectId: data.data.project.id,
        projectLabel: data.data.project.name,
        sessionId: [],
        masterKey: '',
      })
    )
    authProcedure({
      tokenPayload: data.data.user as TokenPayloadInterface,
      showcaseRoom: data.data.showcaseRoom,
    })
  }

  useEffect(() => {
    runAuthWithCacheVersion()
  }, [data])

  useEffect(() => {
    handleAutoAuthProcedure()
  }, [tokenData])

  useEffect(() => {
    if (error) {
      if ('data' in error) {
        setServerErrorMessage(
          JSON.parse(JSON.stringify(error.data)).message ?? ''
        )
      }
      setAuthFail(true)
      setIsLoading(false)
    }
  }, [error])

  useEffect(() => {
    if (tokenError) {
      if ('data' in tokenError) {
        setServerErrorMessage(
          JSON.parse(JSON.stringify(tokenError.data)).message ?? ''
        )
      }
      setAuthFail(true)
      setIsLoading(false)
    }
  }, [tokenError])

  useEffect(() => {
    resetUtil.resetStore()
    localStorage.removeItem('offlineMode')
  }, [])

  const autoAuthenticate = async (projectId: string) => {
    setIsLoading(true)
    setAuthFail(false)

    const exists = await firebaseControlQuery.isProjectEnabled(projectId)
    if (!exists) {
      setIsLoading(false)
      setAuthFail(true)
      return
    }

    const clientInformation = await getClientInformation()
    const { authenticate } = getQueryStringParams(location.search)
    if (authenticate && projectId) {
      authenticateViaToken({
        projectId,
        token: authenticate,
        clientInformation: { ...clientInformation, client: 'showcase' },
      })
    }
    setIsLoading(false)
  }

  useEffect(() => {
    const paths = location.pathname.split('/').filter((res: string) => res)
    if (paths.length) {
      setProjectName({
        ...projectName,
        value: paths[0],
      })
      autoAuthenticate(paths[0])
    }
  }, [])

  return (
    <Container
      background={{
        url: '/images/devsuite-bg.jpg',
        type: 'new',
        noSpliceUrl: true,
      }}
      disableNavigation
    >
      {qrAuth ? (
        <QRAuth />
      ) : (
        <div className="flex h-screen w-full flex-col items-center justify-center gap-8">
          <form
            className="basic-white-form flex flex-col gap-8"
            onSubmit={login}
          >
            <h2 className="text-center">Showcase</h2>

            <div>
              <label className="input-label" htmlFor="projectName">
                Display ID
              </label>
              <input
                onChange={handleProjectNameChange}
                onFocus={handleProjectNameFocus}
                onBlur={handleProjectNameBlur}
                className={`focus:shadow-outline max-h-[38px] !px-[13px] !py-[10px] focus:outline-none ${
                  projectName.isValid ? 'input-gray' : 'input-gray-error'
                }`}
                id="projectName"
                type="text"
                placeholder="id-development"
                value={projectName.value}
                disabled={isLoading}
                required
              />
              {projectName.message && (
                <div>
                  <p className="text-xs text-red-500">{projectName.message}</p>
                </div>
              )}
            </div>

            <div>
              <label className="input-label" htmlFor="username">
                Username
              </label>
              <input
                onChange={handleEmailChange}
                onFocus={handleEmailFocus}
                onBlur={handleEmailBlur}
                className={`focus:shadow-outline max-h-[38px] !px-[13px] !py-[10px] focus:outline-none ${
                  email.isValid ? 'input-gray' : 'input-gray-error'
                }`}
                id="username"
                type="email"
                placeholder="example@gmail.com"
                value={email.value}
                disabled={isLoading}
                required
              />
              {email.message && (
                <div>
                  <p className="text-xs text-red-500">
                    {email.value ? 'Invalid Email Format.' : email.message}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="input-label" htmlFor="password">
                Password
              </label>
              <input
                onChange={handlePasswordChange}
                onFocus={handlePasswordFocus}
                onBlur={handlePasswordBlur}
                className={`focus:shadow-outline max-h-[38px] !px-[13px] !py-[10px] focus:outline-none ${
                  password.isValid ? 'input-gray' : 'input-gray-error'
                }`}
                id="password"
                type="password"
                name="password"
                placeholder="********"
                autoComplete="on"
                value={password.value}
                disabled={isLoading}
                required
              />
              {password.message && (
                <div>
                  <p className="text-xs text-red-500">{password.message}</p>
                </div>
              )}
              {!password.message && authFail && (
                <div>
                  <p className="my-2 text-xs text-red-500">
                    {serverErrorMessage === 'Invalid Display ID'
                      ? 'Invalid Display ID'
                      : 'You entered invalid user credentials.'}
                  </p>
                </div>
              )}
            </div>

            <div>
              <button
                className={`focus:shadow-outline w-full rounded px-4 py-2 text-white focus:outline-none ${
                  formValid
                    ? 'bg-sky-500 hover:bg-sky-700'
                    : 'bg-gray-500 hover:bg-gray-700'
                }`}
                type="submit"
                disabled={!formValid || isLoading}
              >
                {isLoading && (
                  <div
                    style={{ borderTopColor: 'transparent' }}
                    className="border-white-400 mr-1 inline-block h-3 w-3 animate-spin rounded-full border-2 border-dotted"
                  />
                )}
                Sign in
              </button>
              <div className="mt-2 px-4 py-2 text-center">
                <Link
                  to="?qrAuth=true"
                  className="text-base text-sky-500 hover:text-sky-700"
                >
                  Use QR code to Sign in
                </Link>
              </div>
            </div>
          </form>
          <div className="flex w-auto min-w-300px flex-col gap-1 text-center">
            <p className="text-center align-middle text-[14px] font-normal leading-[20px] tracking-normal text-white">
              View buyer shortlist analytics?
            </p>
            <a
              href={ANALYTICS_DASHBOARD_REDIRECT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 text-[16px] font-normal leading-[24px] tracking-normal text-[#52C2F5] hover:text-sky-700"
            >
              Go to Analytics Dashboard
              <LinkSvg height="16" width="16" size="m" />
            </a>
          </div>
        </div>
      )}
    </Container>
  )
}

export default connect(({ projectIdentity }: RootStateTypeExtra) => ({
  projectIdentity,
}))(Login)
